import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, money, fdt, fdate } from "../api";
import { JobPaymentCard, JobReviewCard } from "../components/JobPaymentReview";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import MapView from "../components/MapView";
import InfoTip from "../components/InfoTip";
import { StatusPill, VEHICLE_LABELS, SERVICE_LABELS } from "../components/bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Star, MapPin, Copy, AlertTriangle, Send, PenLine } from "lucide-react";

const inputCls = "cr-input";

const NEXT_STATUS = {
  assigned: ["picked_up", "in_transit"],
  picked_up: ["in_transit", "delivered"],
  in_transit: ["delivered"],
};

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0a0a0c"; ctx.lineWidth = 2; ctx.lineCap = "round";
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return [p.clientX - r.left, p.clientY - r.top];
    };
    const start = (e) => { drawing.current = true; ctx.beginPath(); ctx.moveTo(...pos(e)); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; ctx.lineTo(...pos(e)); ctx.stroke(); e.preventDefault(); };
    const end = () => { drawing.current = false; onChange(c.toDataURL("image/png")); };
    c.addEventListener("pointerdown", start); c.addEventListener("pointermove", move);
    c.addEventListener("pointerup", end); c.addEventListener("pointerleave", end);
    return () => { c.removeEventListener("pointerdown", start); c.removeEventListener("pointermove", move); c.removeEventListener("pointerup", end); c.removeEventListener("pointerleave", end); };
  }, [onChange]);
  return (
    <div>
      <canvas ref={ref} data-testid="signature-pad" width={480} height={160}
        className="w-full h-40 border border-dashed border-zinc-300 rounded-lg bg-white touch-none" />
      <button type="button" data-testid="signature-clear" onClick={() => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(null); }}
        className="text-xs text-zinc-500 mt-1 hover:text-zinc-800">Clear signature</button>
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [route, setRoute] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [bidOpen, setBidOpen] = useState(false);
  const [bid, setBid] = useState({ amount: "", eta_minutes: "", message: "" });
  const [podOpen, setPodOpen] = useState(false);
  const [pod, setPod] = useState({ recipient_name: "", note: "", signature_data: null, photo_data: null });
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [rating, setRating] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      if (data.pickup?.lat && data.delivery?.lat && !route) {
        api.post("/maps/route", { pickup: { lat: data.pickup.lat, lng: data.pickup.lng }, delivery: { lat: data.delivery.lat, lng: data.delivery.lng } })
          .then((r) => setRoute(r.data.geometry)).catch(() => {});
      }
      api.get(`/jobs/${id}/messages`).then((r) => setMessages(r.data.messages)).catch(() => {});
    } catch (e) { toast.error(fmtErr(e)); }
  }, [id, route]);

  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  if (!job) return <div className="max-w-5xl mx-auto px-4 py-20 text-center text-sm text-zinc-400">Loading job…</div>;

  const isOwner = user?.role === "company" && job.company_id === user.id;
  const isCourier = user?.role === "courier" && job.assigned_courier_id === user.id;
  const isAdmin = user?.role === "admin";

  const withGeo = (fn) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => fn(p.coords.latitude, p.coords.longitude),
        () => fn(null, null), { timeout: 5000 });
    } else fn(null, null);
  };

  const setStatus = async (status) => {
    withGeo(async (lat, lng) => {
      try {
        await api.post(`/jobs/${id}/status`, { status, lat, lng });
        toast.success(`Status: ${status.replace(/_/g, " ")}`);
        load();
      } catch (e) { toast.error(fmtErr(e)); }
    });
  };

  const placeBid = async () => {
    try {
      await api.post(`/jobs/${id}/bids`, { amount: +bid.amount, eta_minutes: bid.eta_minutes ? +bid.eta_minutes : null, message: bid.message || null });
      toast.success("Bid placed"); setBidOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const acceptBid = async (bidId) => {
    try { await api.post(`/bids/${bidId}/accept`); toast.success("Bid accepted — courier assigned"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const submitPod = async () => {
    try {
      await api.post(`/jobs/${id}/pod`, pod);
      toast.success("Proof of delivery saved"); setPodOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    try { await api.post(`/jobs/${id}/messages`, { text: msgText }); setMsgText(""); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const openDispute = async () => {
    try { await api.post("/disputes", { job_id: id, reason: disputeReason }); toast.success("Dispute opened"); setDisputeOpen(false); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const photoToB64 = (file) => {
    const r = new FileReader();
    r.onload = () => setPod((p) => ({ ...p, photo_data: r.result }));
    r.readAsDataURL(file);
  };

  const driver = job.courier_location ? [{ driver_id: "courier", lat: job.courier_location.lat, lng: job.courier_location.lng, name: job.assigned_courier_name }] : [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="job-detail">
      <Seo title={`Job ${job.ref} | CourierRadar`} noindex />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-mono">{job.ref}</h1>
            <StatusPill status={job.status} />
          </div>
          <p className="text-xs text-zinc-500 mt-1">{SERVICE_LABELS[job.service_level]} · {VEHICLE_LABELS[job.vehicle_type]} · created {fdt(job.created_at)}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="copy-tracking-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/track?ref=${job.ref}`); toast.success("Tracking link copied"); }}
            className="border border-zinc-300 hover:bg-zinc-50 text-zinc-800 font-medium transition-all px-4 py-2 rounded-lg text-sm flex items-center gap-1.5">
            <Copy className="w-3.5 h-3.5" /> Tracking link
          </button>
          {(isOwner || isCourier) && ["assigned", "picked_up", "in_transit", "delivered"].includes(job.status) && (
            <button data-testid="open-dispute-btn" onClick={() => setDisputeOpen(true)}
              className="border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-all px-4 py-2 rounded-lg text-sm flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Dispute
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 items-start">
        <div className="lg:col-span-3 space-y-6">
          <div className="rounded-xl overflow-hidden border border-black/5" data-testid="job-itinerary-map">
            <MapView height="360px" route={route} drivers={driver}
              endpoints={[job.pickup, job.delivery].filter((p) => p?.lat != null && p?.lng != null)} />
          </div>

          {(isCourier || isAdmin) && NEXT_STATUS[job.status]?.length > 0 && (
            <div className="cr-card !p-5">
              <h2 className="text-sm font-bold mb-3 flex items-center">Update status<InfoTip text="Your device GPS position is attached to each update for live tracking." /></h2>
              <div className="flex flex-wrap gap-2">
                {NEXT_STATUS[job.status].map((s) => (
                  <button key={s} data-testid={`status-btn-${s}`} onClick={() => setStatus(s)}
                    className="bg-black text-white hover:bg-zinc-800 active:scale-[0.98] font-medium transition-all px-5 py-2.5 rounded-lg text-sm">
                    {s === "picked_up" ? "Picked up" : s === "in_transit" ? "In transit" : "Delivered"}
                  </button>
                ))}
                {(job.status === "in_transit" || job.status === "picked_up") && (
                  <button data-testid="pod-open-btn" onClick={() => setPodOpen(true)}
                    className="bg-[#0A0A0A] text-white hover:bg-[#26272A] font-semibold transition-all px-5 py-2.5 rounded-lg text-sm flex items-center gap-1.5">
                    <PenLine className="w-4 h-4" /> Proof of delivery
                  </button>
                )}
              </div>
            </div>
          )}

          {user?.role === "courier" && job.status === "open" && !isCourier && (
            <div className="cr-card !p-5 flex flex-wrap gap-3">
              {job.pricing_mode === "fixed" ? (
                <button data-testid="instant-accept-btn" onClick={async () => { try { await api.post(`/jobs/${id}/accept`); toast.success("Job assigned to you"); load(); } catch (e) { toast.error(fmtErr(e)); } }}
                  className="bg-[#0A0A0A] text-white hover:bg-[#26272A] font-semibold transition-all px-6 py-3 rounded-lg text-sm">
                  Accept for {money(job.price_gross ?? job.price_net, job.currency)}
                </button>
              ) : (
                <button data-testid="place-bid-btn" onClick={() => setBidOpen(true)}
                  className="bg-black text-white hover:bg-zinc-800 font-medium transition-all px-6 py-3 rounded-lg text-sm">Place a bid</button>
              )}
            </div>
          )}

          {job.pod && (
            <div className="cr-card !p-5" data-testid="pod-section">
              <h2 className="text-sm font-bold mb-3">Proof of delivery</h2>
              <p className="text-xs text-zinc-500">Received by <strong>{job.pod.recipient_name}</strong> · {fdt(job.pod.at)}</p>
              <div className="flex gap-3 mt-3">
                {job.pod.signature_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${job.pod.signature_url}`} alt="Recipient signature" className="h-20 border border-zinc-200 rounded-lg" />}
                {job.pod.photo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${job.pod.photo_url}`} alt="Delivery photo" className="h-20 border border-zinc-200 rounded-lg object-cover" />}
              </div>
            </div>
          )}

          <JobPaymentCard job={job} onChange={load} />
          <JobReviewCard job={job} />

          {["assigned", "picked_up", "in_transit", "delivered", "disputed"].includes(job.status) && (
          <div className="cr-card !p-5" data-testid="messages-section">
            <h2 className="text-sm font-bold mb-3">Messages</h2>
            <div className="space-y-2.5 max-h-64 overflow-y-auto mb-3">
              {messages.length === 0 && <p className="text-xs text-zinc-400">No messages yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.from_id === user?.id ? "justify-end" : ""}`}>
                  <div className={`max-w-[80%] rounded-xl px-3.5 py-2 text-sm ${m.from_id === user?.id ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-800"}`}>
                    <p className="text-[10px] opacity-60 mb-0.5">{m.from_name} · {fdt(m.at)}</p>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="message-input" value={msgText} onChange={(e) => setMsgText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder="Write a message…" className={inputCls} />
              <button data-testid="message-send-btn" onClick={sendMessage} className="bg-black text-white rounded-lg px-4 hover:bg-zinc-800 transition-colors"><Send className="w-4 h-4" /></button>
            </div>
          </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="cr-card !p-5">
            <h2 className="text-sm font-bold mb-4">Details</h2>
            <dl className="space-y-3 text-sm">
              <div><dt className="cr-label">Pickup</dt>
                <dd className="text-zinc-800 flex items-start gap-1.5 mt-0.5"><MapPin className="w-3.5 h-3.5 text-[#0A0A0A] mt-0.5 shrink-0" />{job.pickup?.address || "—"}</dd>
                {job.pickup_date && <dd className="text-xs text-zinc-500 mt-0.5">{fdate(job.pickup_date)}</dd>}</div>
              <div><dt className="cr-label">Delivery</dt>
                <dd className="text-zinc-800 flex items-start gap-1.5 mt-0.5"><MapPin className="w-3.5 h-3.5 text-zinc-900 mt-0.5 shrink-0" />{job.delivery?.address || "—"}</dd>
                {job.delivery_date && <dd className="text-xs text-zinc-500 mt-0.5">{fdate(job.delivery_date)}</dd>}</div>
              {job.cargo?.description && <div><dt className="cr-label">Cargo</dt>
                <dd className="text-zinc-800 mt-0.5">{job.cargo.description}</dd>
                <dd className="text-xs text-zinc-500">{[job.cargo.weight_kg && `${job.cargo.weight_kg} kg`, job.cargo.pallets && `${job.cargo.pallets} pallets`, job.cargo.quantity && `${job.cargo.quantity} items`].filter(Boolean).join(" · ")}</dd></div>}
              <div><dt className="cr-label">Price (ex VAT)</dt>
                <dd className="text-zinc-900 font-bold mt-0.5">{job.final_price ? money(job.final_price, job.currency) : job.pricing_mode === "bidding" ? "Open for bids" : money(job.price_net ?? job.price_gross, job.currency)}</dd></div>
              {job.distance_km && <div><dt className="cr-label">Distance</dt>
                <dd className="text-zinc-800 mt-0.5">{job.distance_km} km</dd></div>}
              {job.assigned_courier_name && <div><dt className="cr-label">Courier</dt>
                <dd className="text-zinc-800 mt-0.5">{job.assigned_courier_name}</dd></div>}
              {isAdmin && job.source !== "manual" && <div><dt className="cr-label">Source</dt>
                <dd className="text-zinc-800 mt-0.5 capitalize">{job.source}{job.source_ref ? ` · ${job.source_ref}` : ""}</dd></div>}
            </dl>
          </div>

          <div className="cr-card !p-5">
            <h2 className="text-sm font-bold mb-4">Timeline</h2>
            <div className="space-y-0" data-testid="job-timeline">
              {(job.timeline || []).map((t, i) => (
                <div key={i} className="flex gap-3 pb-4 last:pb-0 relative">
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full mt-1 ${i === job.timeline.length - 1 ? "bg-[#0A0A0A]" : "bg-zinc-300"}`} />
                    {i < job.timeline.length - 1 && <div className="w-px flex-1 bg-zinc-200 mt-1" />}
                  </div>
                  <div className="pb-1">
                    <p className="text-sm font-semibold capitalize">{t.status.replace(/_/g, " ")}</p>
                    {t.note && <p className="text-xs text-zinc-500">{t.note}</p>}
                    <p className="text-[10px] text-zinc-400 mt-0.5">{fdt(t.at)}{t.by ? ` · ${t.by}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>

            {(isOwner || isAdmin) && job.bids && (
              <div className="mt-5 pt-5 border-t border-[#F6F3EA]" data-testid="bids-section">
                <h2 className="text-sm font-bold mb-4">Bids ({job.bids.length})</h2>
              {job.bids.length === 0 && <p className="text-xs text-zinc-400">No bids yet.</p>}
              <div className="space-y-3 max-h-[400px] cr-scroll pr-1">
                {job.bids.map((b) => (
                  <div key={b.id} data-testid={`bid-${b.id}`} className="border border-zinc-100 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">{b.courier_name}</span>
                      <span className="text-sm font-bold">{money(b.amount, b.currency)}</span>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {b.courier_rating ? `★ ${b.courier_rating} · ` : ""}{VEHICLE_LABELS[b.vehicle_type] || ""}{b.eta_minutes ? ` · ETA ${b.eta_minutes} min` : ""}
                    </p>
                    {b.message && <p className="text-xs text-zinc-600 mt-1.5">{b.message}</p>}
                    {b.status === "pending" && job.status === "open" && (
                      <div className="flex gap-2 mt-2.5">
                        <button data-testid={`accept-bid-${b.id}`} onClick={() => acceptBid(b.id)}
                          className="bg-[#0A0A0A] text-white text-xs font-semibold px-3.5 py-1.5 rounded-md hover:bg-[#26272A] transition-colors">Accept</button>
                        <button data-testid={`decline-bid-${b.id}`} onClick={async () => { await api.post(`/bids/${b.id}/decline`); load(); }}
                          className="border border-zinc-300 text-xs font-medium px-3.5 py-1.5 rounded-md hover:bg-zinc-50 transition-colors">Decline</button>
                      </div>
                    )}
                    {b.status !== "pending" && <StatusPill status={b.status} />}
                  </div>
                ))}
                </div>
              </div>
              )}
          </div>

          {isOwner && job.status === "delivered" && !job.rating && (
            <div className="cr-card !p-5" data-testid="rating-section">
              <h2 className="text-sm font-bold mb-2">Rate the courier</h2>
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} data-testid={`rate-star-${s}`} onClick={() => setRating(s)}>
                    <Star className={`w-6 h-6 ${rating >= s ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-zinc-300"}`} />
                  </button>
                ))}
              </div>
              <button data-testid="rate-submit-btn" disabled={!rating}
                onClick={async () => { try { await api.post(`/jobs/${id}/rate`, { score: rating }); toast.success("Thanks for rating"); load(); } catch (e) { toast.error(fmtErr(e)); } }}
                className="bg-black text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-zinc-800 disabled:opacity-40">Submit rating</button>
            </div>
          )}

          {isOwner && ["open", "assigned"].includes(job.status) && (
            <button data-testid="cancel-job-btn" onClick={async () => { try { await api.post(`/jobs/${id}/status`, { status: "cancelled" }); toast.success("Job cancelled"); load(); } catch (e) { toast.error(fmtErr(e)); } }}
              className="w-full border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-all px-4 py-2.5 rounded-lg text-sm">Cancel job</button>
          )}
        </div>
      </div>

      <Dialog open={bidOpen} onOpenChange={setBidOpen}>
        <DialogContent data-testid="bid-dialog">
          <DialogHeader><DialogTitle>Place a bid on {job.ref}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="text-xs font-semibold text-[#78756E] uppercase tracking-wide">Your price ({job.currency || "GBP"})</label>
              <input data-testid="bid-amount" type="number" min="1" step="0.01" value={bid.amount} onChange={(e) => setBid({ ...bid, amount: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs font-semibold text-[#78756E] uppercase tracking-wide">Pickup ETA (minutes)</label>
              <input data-testid="bid-eta" type="number" min="0" value={bid.eta_minutes} onChange={(e) => setBid({ ...bid, eta_minutes: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs font-semibold text-[#78756E] uppercase tracking-wide">Message (optional)</label>
              <textarea data-testid="bid-message" value={bid.message} onChange={(e) => setBid({ ...bid, message: e.target.value })} rows={2} className={inputCls} /></div>
            <button data-testid="bid-submit-btn" onClick={placeBid} className="w-full bg-[#0A0A0A] text-white hover:bg-[#26272A] font-semibold transition-all px-5 py-2.5 rounded-lg text-sm">Submit bid</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={podOpen} onOpenChange={setPodOpen}>
        <DialogContent data-testid="pod-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Proof of delivery</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="text-xs font-semibold text-[#78756E] uppercase tracking-wide">Received by (name)</label>
              <input data-testid="pod-recipient" value={pod.recipient_name} onChange={(e) => setPod({ ...pod, recipient_name: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs font-semibold text-[#78756E] uppercase tracking-wide">Signature</label>
              <SignaturePad onChange={(d) => setPod((p) => ({ ...p, signature_data: d }))} /></div>
            <div><label className="text-xs font-semibold text-[#78756E] uppercase tracking-wide">Photo (optional)</label>
              <input data-testid="pod-photo" type="file" accept="image/*" onChange={(e) => e.target.files[0] && photoToB64(e.target.files[0])} className="text-xs" /></div>
            <div><label className="text-xs font-semibold text-[#78756E] uppercase tracking-wide">Note</label>
              <input data-testid="pod-note" value={pod.note} onChange={(e) => setPod({ ...pod, note: e.target.value })} className={inputCls} /></div>
            <button data-testid="pod-submit-btn" onClick={submitPod} disabled={!pod.recipient_name}
              className="w-full bg-black text-white hover:bg-zinc-800 font-medium transition-all px-5 py-2.5 rounded-lg text-sm disabled:opacity-40">Save POD</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent data-testid="dispute-dialog">
          <DialogHeader><DialogTitle>Open a dispute</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <textarea data-testid="dispute-reason" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} rows={3}
              placeholder="Describe the issue…" className={inputCls} />
            <button data-testid="dispute-submit-btn" onClick={openDispute} disabled={disputeReason.length < 3}
              className="w-full bg-red-600 text-white hover:bg-red-700 font-medium transition-all px-5 py-2.5 rounded-lg text-sm disabled:opacity-40">Open dispute</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
